/**
 * OCAMAR — Recomendaciones en el carrito
 *
 * Muestra productos para sumar al pedido. El orden de intentos es a propósito:
 *   1. /recommendations/products.json?intent=related       (motor de Shopify)
 *   2. /recommendations/products.json?intent=complementary (motor de Shopify)
 *   3. Respaldo servido por Liquid en el snippet.
 *
 * El paso 3 existe porque el motor de Shopify aprende del historial de compras.
 * En una tienda nueva devuelve una lista vacía y la tira desaparecía: por eso
 * antes "no aparecían las recomendaciones".
 */
(function () {
  'use strict';

  function money(cents) {
    try {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'COP',
        maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return '$' + Math.round(cents / 100).toLocaleString('es-CO');
    }
  }

  function sized(url, px) {
    if (!url) return '';
    if (url.indexOf('width=') !== -1 || /_\d+x\./.test(url)) return url;
    return String(url).replace(/(\.[a-z]+)(\?|$)/i, '_' + px + 'x$1$2');
  }

  async function askShopify(productId, intent, limit) {
    try {
      var res = await fetch(
        '/recommendations/products.json?product_id=' + productId +
        '&limit=' + limit + '&intent=' + intent
      );
      if (!res.ok) return [];
      var data = await res.json();
      return (data && data.products) || [];
    } catch (e) {
      return [];
    }
  }

  class OcamarCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.limit = Number(this.getAttribute('limit') || 10);
      this.refresh = this.refresh.bind(this);
      this.renderedKey = null;
      this.fallback = this.readFallback();

      this.refresh();

      /* El tema dispara varios eventos al cambiar el carrito; escuchamos los comunes. */
      this.eventNames = ['cart:update', 'cart:refresh', 'CartUpdate', 'cart:change', 'cart-update'];
      this.eventNames.forEach(
        function (name) { document.addEventListener(name, this.refresh); }.bind(this)
      );
    }

    disconnectedCallback() {
      (this.eventNames || []).forEach(
        function (name) { document.removeEventListener(name, this.refresh); }.bind(this)
      );
    }

    readFallback() {
      var node = this.querySelector('[data-ocamar-reco-fallback]');
      if (!node) return [];
      try {
        var list = JSON.parse(node.textContent);
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    }

    async refresh() {
      var cart;
      try {
        cart = await fetch('/cart.js').then(function (res) { return res.json(); });
      } catch (e) {
        this.hidden = true;
        return;
      }

      if (!cart || !cart.items || cart.items.length === 0) {
        this.hidden = true;
        return;
      }

      /* Tomamos como referencia el ultimo producto agregado. */
      var seed = cart.items[cart.items.length - 1];
      var inCart = cart.items.map(function (item) { return item.product_id; });

      /* La clave incluye el conteo: si cambia el carrito, recalculamos.
         Antes solo miraba el producto y por eso se quedaba pegado. */
      var key = seed.product_id + '|' + cart.item_count;
      if (this.renderedKey === key && !this.hidden) return;

      var pool = await askShopify(seed.product_id, 'related', this.limit);

      if (this.usable(pool, inCart).length < 2) {
        var extra = await askShopify(seed.product_id, 'complementary', this.limit);
        pool = pool.concat(extra);
      }

      if (this.usable(pool, inCart).length < 2) {
        pool = pool.concat(this.fallback);
      }

      var products = this.usable(pool, inCart).slice(0, 6);

      if (products.length === 0) {
        this.hidden = true;
        return;
      }

      this.renderedKey = key;
      this.render(products);
    }

    /** Quita repetidos, lo que ya esta en el carrito y lo agotado. */
    usable(list, inCart) {
      var seen = {};
      return list.filter(function (product) {
        if (!product || !product.id) return false;
        if (seen[product.id]) return false;
        if (inCart.indexOf(product.id) !== -1) return false;
        if (product.available === false) return false;
        seen[product.id] = true;
        return true;
      });
    }

    render(products) {
      var track = this.querySelector('[data-track]');
      if (!track) return;

      this.hidden = false;

      track.innerHTML = products
        .map(function (product) {
          var image = sized(product.featured_image, 300);
          var variantId = product.variant_id ||
            (product.variants && product.variants[0] ? product.variants[0].id : null);
          return [
            '<li class="ocamar-reco__item">',
            '  <a class="ocamar-reco__media" href="' + product.url + '" tabindex="-1" aria-hidden="true">',
            image
              ? '    <img src="' + image + '" alt="" loading="lazy" width="150" height="150">'
              : '    <span class="ocamar-reco__media-empty"></span>',
            '  </a>',
            '  <div class="ocamar-reco__info">',
            '    <a class="ocamar-reco__title" href="' + product.url + '">' + product.title + '</a>',
            '    <span class="ocamar-reco__price">' + money(product.price) + '</span>',
            variantId
              ? '    <button type="button" class="ocamar-reco__add" data-add="' + variantId + '">Agregar</button>'
              : '',
            '  </div>',
            '</li>'
          ].join('\n');
        })
        .join('\n');

      var self = this;
      track.querySelectorAll('[data-add]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.add(btn); });
      });
    }

    async add(btn) {
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Agregando…';

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(btn.getAttribute('data-add')), quantity: 1 }],
            sections: 'cart-drawer-section'
          })
        });
        var data = await res.json();

        var refreshed = this.swapDrawer(data && data.sections && data.sections['cart-drawer-section']);

        this.renderedKey = null;
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

        if (refreshed) {
          btn.textContent = 'Agregado';
          this.refresh();
        } else {
          /* Si no pudimos refrescar el cajon en caliente, recargamos. */
          window.location.reload();
        }
      } catch (e) {
        btn.disabled = false;
        btn.textContent = original;
      }
    }

    /**
     * Cambia solo los items, el resumen y el contador del cajon abierto.
     * No tocamos el <dialog> para que el carrito no se cierre solo.
     */
    swapDrawer(html) {
      if (!html) return false;
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var done = false;

        ['.cart-drawer__items', '.cart-drawer__summary'].forEach(function (sel) {
          var fresh = doc.querySelector(sel);
          var live = document.querySelector(sel);
          if (fresh && live) {
            live.innerHTML = fresh.innerHTML;
            done = true;
          }
        });

        var freshBadge = doc.querySelector('.theme-drawer__badge');
        var liveBadge = document.querySelector('#cart-drawer .theme-drawer__badge');
        if (freshBadge && liveBadge) liveBadge.textContent = freshBadge.textContent;

        return done;
      } catch (e) {
        return false;
      }
    }
  }

  if (!customElements.get('ocamar-cart-recommendations')) {
    customElements.define('ocamar-cart-recommendations', OcamarCartRecommendations);
  }
})();
